import { ArrowUpRight, CheckCircle2, Play, Smartphone, UsersRound } from "lucide-react";
import { APP_LINKS } from "../../lib/site";
import { Reveal } from "./primitives";

const APPS = [APP_LINKS.customer, APP_LINKS.partner];

const ICONS = [Smartphone, UsersRound];

export const DownloadApps = () => (
  <section
    id="apps"
    className="relative overflow-hidden bg-cream py-20 sm:py-28 lg:py-32"
    data-testid="download-apps-section"
  >
    <div className="pointer-events-none absolute left-0 top-10 h-80 w-80 rounded-full bg-amber/15 blur-3xl" />
    <div className="pointer-events-none absolute bottom-0 right-0 h-96 w-96 rounded-full bg-teal/10 blur-3xl" />

    <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="grid items-end gap-5 lg:grid-cols-[1fr_auto]">
        <div>
          <Reveal>
            <p className="eyebrow mb-4 text-amber">Get the apps</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="display-serif max-w-[13ch] text-4xl leading-[1.02] text-navy sm:text-5xl lg:text-[3.5rem]">
              SunShell One is live on Android.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.12}>
          <p className="max-w-[42ch] text-lg leading-relaxed text-navy/65">
            Customers and partners now have dedicated mobile workflows for the full solar journey.
          </p>
        </Reveal>
      </div>

      <div id="apps-download" className="mt-10 grid scroll-mt-24 items-stretch gap-5 sm:mt-12 lg:grid-cols-2 lg:gap-6">
        {APPS.map((app, index) => (
          <AppCard key={app.audience} app={app} index={index} Icon={ICONS[index]} />
        ))}
      </div>
    </div>
  </section>
);

const AppCard = ({ app, index, Icon }) => (
  <Reveal delay={index * 0.08} className="h-full">
    <article className="group relative h-full overflow-hidden rounded-[1.5rem] border border-navy/10 bg-white shadow-2xl shadow-navy/8 sm:rounded-[2rem]">
      <div className={`absolute inset-x-0 top-0 h-1 ${index === 0 ? "bg-amber" : "bg-teal"}`} />
      <div className="grid h-full gap-6 p-5 sm:p-7 lg:min-h-[500px] lg:grid-cols-[minmax(0,1fr)_190px] lg:items-center xl:grid-cols-[minmax(0,1fr)_210px] xl:p-8">
        <div className="relative z-10 flex h-full flex-col">
          <div className="flex items-center gap-3">
            <span className={`grid h-11 w-11 place-items-center rounded-2xl sm:h-12 sm:w-12 ${index === 0 ? "bg-amber/12 text-amber" : "bg-teal/12 text-teal"}`}>
              <Icon size={22} strokeWidth={1.9} />
            </span>
            <div>
              <p className="eyebrow text-navy/45">{app.audience}</p>
              <p className="mt-1 text-sm font-semibold text-navy">{app.label}</p>
            </div>
          </div>

          <h3 className="display-serif mt-6 max-w-[12ch] text-3xl leading-tight text-navy sm:min-h-[7.6rem] sm:text-4xl lg:min-h-[8.1rem] lg:text-[2.35rem]">
            {app.title}
          </h3>
          <p className="mt-4 max-w-[38ch] text-base leading-relaxed text-navy/65 sm:min-h-[6.6rem] lg:text-[1.02rem]">
            {app.description}
          </p>

          <ul className="mt-6 space-y-3">
            {app.highlights.map((item) => (
              <li key={item} className="flex items-center gap-3 text-sm font-semibold text-navy/70">
                <CheckCircle2 size={18} className={`shrink-0 ${index === 0 ? "text-amber" : "text-teal"}`} />
                {item}
              </li>
            ))}
          </ul>

          <div className="mt-7 lg:mt-auto lg:pt-8">
            <PlayStoreButton href={app.url} label={app.cta} dark={index === 1} className="w-full justify-center sm:w-auto" />
          </div>
        </div>

        <div className="relative mx-auto w-[150px] shrink-0 sm:w-[185px] lg:mx-0 lg:w-full">
          <div className={`absolute -inset-6 rounded-[2rem] blur-2xl ${index === 0 ? "bg-amber/20" : "bg-teal/20"}`} />
          <div className="relative rounded-[1.8rem] border-[7px] border-navy-900 bg-navy-900 shadow-2xl shadow-navy/20 ring-1 ring-white/30">
            <div className="absolute left-1/2 top-1.5 z-20 h-3.5 w-16 -translate-x-1/2 rounded-full bg-navy-900" />
            <div className="overflow-hidden rounded-[1.55rem]">
              <img
                src={app.screenshotUrl}
                alt={`${app.label} mobile app preview`}
                className="block h-auto w-full"
              />
            </div>
          </div>
        </div>
      </div>
    </article>
  </Reveal>
);

export const PlayStoreButton = ({ href, label, dark = false, className = "" }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`inline-flex min-h-[56px] items-center gap-3 rounded-2xl px-5 py-3 text-left shadow-lg transition-[transform,background-color] duration-300 hover:-translate-y-0.5 ${
      dark
        ? "bg-navy text-white shadow-navy/20 hover:bg-navy-800"
        : "bg-amber text-white shadow-amber/25 hover:bg-amber-600"
    } ${className}`}
  >
    <Play size={23} fill="currentColor" strokeWidth={1.6} className="shrink-0" />
    <span>
      <span className="block text-[0.68rem] font-semibold uppercase leading-none opacity-75">Get it on</span>
      <span className="mt-1 block text-sm font-bold leading-tight sm:text-base">{label}</span>
    </span>
    <ArrowUpRight size={17} className="ml-1 shrink-0" />
  </a>
);
